import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EligibleItem } from '../../services/m5m1Api';
import { m5m1Api } from '../../services/m5m1Api';
import { BatchAcceptanceBridge, pwm5m1f1AcceptanceManifest } from '../../features/acceptance';
import { useM5M1Reversal } from './hooks/useM5M1Reversal';
import './M5M1Form.css';

const { Title, Text } = Typography;

function renderStatus(status: string): React.ReactNode {
  if (status === 'P') return <Tag color="blue">已核准</Tag>;
  if (status === 'N') return <Tag color="red">未核准</Tag>;
  return <Tag color="default">{status || '未知'}</Tag>;
}

const M5M1Form: React.FC = () => {
  const {
    loading,
    executing,
    items,
    total,
    selectedKeys,
    lastApiResponse,
    filter,
    setFilter,
    setSelectedKeys,
    fetchEligible,
    batchExecute,
  } = useM5M1Reversal();

  const columns: ColumnsType<EligibleItem> = [
    {
      title: <span data-contract-field="fm110_01">異動調整單號</span>,
      dataIndex: 'fm110_01',
      key: 'fm110_01',
      width: 120,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: <span data-contract-field="fm110_11">日期</span>,
      dataIndex: 'fm110_11',
      key: 'fm110_11',
      width: 110,
      render: (date: string) => date || '\u2014',
    },
    {
      title: <span data-contract-field="fm110_90">版本</span>,
      dataIndex: 'fm110_90',
      key: 'fm110_90',
      width: 60,
      align: 'center',
    },
    {
      title: <span data-contract-field="fm110_80">狀態</span>,
      dataIndex: 'fm110_80',
      key: 'fm110_80',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  const handleSearch = () => { fetchEligible(); };

  const handleFilterChange = (field: 'startNo' | 'endNo', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  const handleStartBlur = () => {
    if (filter.startNo && !filter.endNo) {
      const autoEnd = (filter.startNo + 'zzzzzzzz').substring(0, 7);
      setFilter({ ...filter, endNo: autoEnd });
    }
  };
  const queryAcceptanceExact = React.useCallback(
    (id: string) => m5m1Api.search({ startNo: id, endNo: id }),
    [],
  );

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>正常產品異動調整核准沖銷 (pwm5m1f1)</Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="fm110_01">單號起始</span>}>
            <Input
              value={filter.startNo}
              onChange={(e) => handleFilterChange('startNo', e.target.value)}
              onBlur={handleStartBlur}
              placeholder="例：FM00001"
              style={{ width: 120 }}
              maxLength={7}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="fm110_01">單號結束</span>}>
            <Input
              value={filter.endNo}
              onChange={(e) => handleFilterChange('endNo', e.target.value)}
              placeholder="例：FM99999"
              style={{ width: 120 }}
              maxLength={7}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              data-acceptance-action="PWM5M1-ACT-QUERY"
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆異動調整單？沖銷後將回復庫存，無法還原。`}
              onConfirm={batchExecute}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <Button
                data-acceptance-action="PWM5M1-ACT-REVERSE"
                danger
                icon={<RollbackOutlined />}
                loading={executing}
                disabled={selectedKeys.length === 0}
              >
                批量沖銷
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      <Table<EligibleItem>
        rowKey="fm110_01"
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 500 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
      <BatchAcceptanceBridge
        manifest={pwm5m1f1AcceptanceManifest}
        records={items}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.fm110_01}
        queryExact={queryAcceptanceExact}
        lastApiResponse={lastApiResponse}
        loading={loading || executing}
        onAutoFill={() => { if (items[0]) setSelectedKeys([items[0].fm110_01]); }}
        autoFillDisabled={items.length === 0}
        autoFillReason="請先查詢可逆沖調整單；自動填表只會勾選第一筆範例，不會執行逆沖。"
        screenDraft={{ filter, selected_items: selectedKeys, candidates: items }}
      />
    </div>
  );
};

export default M5M1Form;
